import { Bot, ExternalLink } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { IconSectionHeader } from '@/components/shared/IconSectionHeader';
import { MonoBadge } from '@/components/shared/MonoBadge';
import { PageDescription, PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import type { PluginManifest } from '@/lib/plugin-registry';

export function PluginAgentSection({ plugin }: { plugin: PluginManifest }) {
  const agentType = plugin.web?.agentType;
  const agentProvider = plugin.server?.agentProvider;

  if (!agentType) return null;

  return (
    <section data-testid="plugin-detail-section-agent">
      <IconSectionHeader icon={Bot}>Agent</IconSectionHeader>
      <div className="space-y-4">
        <div className="rounded-lg border p-4">
          <div className="flex min-w-0 items-center gap-2">
            <PanelTitle as="h3" size="sm">
              {agentType.name}
            </PanelTitle>
            <MonoBadge>{agentType.type}</MonoBadge>
            <span className="text-xs text-muted-foreground">{agentType.vendor}</span>
          </div>
          <PageDescription className="text-xs">{agentType.tagline}</PageDescription>
          {agentProvider && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {agentProvider.supportsDevboxSetupHooks && (
                <Badge variant="outline" className="text-3xs font-normal">
                  devbox setup hooks
                </Badge>
              )}
              {agentProvider.supportsAnthropicGateway && (
                <Badge variant="outline" className="text-3xs font-normal">
                  Anthropic gateway
                </Badge>
              )}
              {agentProvider.supportsOpenAIGateway && (
                <Badge variant="outline" className="text-3xs font-normal">
                  OpenAI gateway
                </Badge>
              )}
            </div>
          )}
        </div>
        {agentType.capabilities.length > 0 && (
          <div>
            <SectionLabel as="span" className="mb-2 block">
              Capabilities
            </SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {agentType.capabilities.map((cap) => (
                <Badge key={cap} variant="secondary" className="text-xs font-normal">
                  {cap}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {agentType.models.length > 0 && (
          <div>
            <SectionLabel as="span" className="mb-2 block">
              Models
            </SectionLabel>
            <div className="flex flex-wrap gap-1.5">
              {agentType.models.map((m) => (
                <Badge
                  key={m.id}
                  variant={m.default ? 'default' : 'secondary'}
                  className="gap-1 text-xs font-normal"
                >
                  {m.name}
                  {m.default && <span className="text-3xs opacity-70">(default)</span>}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {agentType.authRequirements.length > 0 && (
          <div>
            <SectionLabel as="span" className="mb-2 block">
              Auth Requirements
            </SectionLabel>
            <div className="divide-y rounded-lg border">
              {agentType.authRequirements.map((req) => (
                <div key={req.name} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <span className="font-mono text-xs">{req.name}</span>
                    <PageDescription className="mt-0.5 text-2xs">{req.description}</PageDescription>
                  </div>
                  <Badge variant="outline" className="shrink-0 text-3xs font-normal">
                    {req.required ? 'required' : 'optional'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {agentType.docsUrl && (
          <a
            href={agentType.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ExternalLink className="h-3 w-3" />
            Documentation
          </a>
        )}
      </div>
    </section>
  );
}
